import Phaser from 'phaser';
import type { GameHarness } from './harness';
import type { DebugFlags } from '../render/debug/DebugOverlay';
import type { Scenario } from '../scenarios/types';
import type { CarInput, WorldState } from '../sim/types';
import { SCENARIOS } from '../scenarios';

/** API exposée sur window.__game en mode test (cf. PLAN.md §11). */
export interface GameTestApi {
  ready: Promise<void>;
  loadScenario(spec: Scenario): Promise<void>;
  setInputs(carIdx: number, input: Partial<CarInput>): void;
  step(frames?: number): void;
  runToFrame(frame: number): void;
  render(): void;
  getState(): WorldState;
  pause(): void;
  resume(): void;
  isPaused(): boolean;
  setDebug(flags: Partial<DebugFlags>): void;
  screenshotReady(): Promise<void>;
  hash(): string;
  reset(): void;
}

export function installTestHook(harness: GameHarness, game: Phaser.Game): void {
  const api: GameTestApi = {
    ready: harness.ready,
    async loadScenario(spec: Scenario): Promise<void> {
      harness.reset(spec);
      harness.manual = true;
    },
    setInputs(carIdx: number, input: Partial<CarInput>): void {
      Object.assign(harness.currentInputs[carIdx], input);
    },
    step(frames = 1): void {
      harness.step(frames);
    },
    runToFrame(frame: number): void {
      while (harness.world.step < frame) harness.tickOnce();
      harness.renderFromState();
    },
    render(): void {
      harness.renderFromState();
    },
    getState(): WorldState {
      return harness.getState();
    },
    pause(): void {
      harness.manual = true;
    },
    resume(): void {
      harness.manual = false;
    },
    isPaused(): boolean {
      return harness.manual;
    },
    setDebug(flags: Partial<DebugFlags>): void {
      harness.setDebug(flags);
    },
    async screenshotReady(): Promise<void> {
      // On attend une vraie passe de rendu (jamais un timeout fixe).
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = (): void => {
          if (!done) {
            done = true;
            resolve();
          }
        };
        game.events.once(Phaser.Core.Events.POST_RENDER, finish);
        requestAnimationFrame(() => requestAnimationFrame(finish));
      });
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (fonts) await fonts.ready;
    },
    hash(): string {
      return harness.hash();
    },
    reset(): void {
      harness.reset(harness.scenario);
      harness.manual = true;
    },
  };

  window.__game = api;
  window.__scenarios = SCENARIOS;
}
